import React, { useRef, useState, useEffect, useCallback } from 'react';
import axiosInstance from '../utils/axiosConfig';
import yonlerImg from '../images/yonler.png';

// ─── Types ────────────────────────────────────────────────────────────────────

type DrawTool = 'rect' | 'oval' | 'line' | 'text' | 'image';

interface DrawShape {
    id: number;
    tool: DrawTool;
    x1: number; y1: number; x2: number; y2: number;
    color: string;
    filled: boolean;
    lineWidth: number;
    text?: string;
    fontSize?: number;
    fontBold?: boolean;
    fontItalic?: boolean;
    imageData?: string;
}

interface KatCizimPanelProps {
    katId: number;
    blokAdi: string;
    katAdi: string;
    katEn: number | null;
    katBoy: number | null;
    blokKatlar?: { katID: number; katAdi: string }[];
    arsaId?: number;
    bolumler?: { bolumID: number; bolumAdi: string; xVal: number | null; yVal: number | null; paylasimlar?: { adSoyad: string; oranYuzde: number }[] }[];
    cepheler?: { cepheAdi: string; yon: string | null }[];
    onBolumKoordinatGuncellendi?: (bolumID: number, xVal: number, yVal: number) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const KatCizimPanel: React.FC<KatCizimPanelProps> = ({ katId, blokAdi, katAdi, katEn, katBoy, blokKatlar, arsaId, bolumler, cepheler, onBolumKoordinatGuncellendi }) => {
    const canvasRef    = useRef<HTMLCanvasElement>(null);
    const wrapperRef   = useRef<HTMLDivElement>(null);
    const textInputRef = useRef<HTMLInputElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const imageCache   = useRef<Map<number, HTMLImageElement>>(new Map());
    const pinPositions = useRef<Map<number, { x: number; y: number }>>(new Map());

    const [tool,       setTool]       = useState<DrawTool>('rect');
    const [color,      setColor]      = useState('#1a2340');
    const [filled,     setFilled]     = useState(false);
    const [fontSize,   setFontSize]   = useState(14);
    const [fontBold,   setFontBold]   = useState(false);
    const [fontItalic, setFontItalic] = useState(false);
    const [shapes,       setShapes]       = useState<DrawShape[]>([]);
    const [isDrawing,    setIsDrawing]    = useState(false);
    const [startPos,     setStartPos]     = useState<{ x: number; y: number } | null>(null);
    const [previewShape, setPreviewShape] = useState<DrawShape | null>(null);
    const [textOverlay, setTextOverlay] = useState(false);
    const [drawPos,     setDrawPos]     = useState<{ x: number; y: number } | null>(null);
    const [overlayPos,  setOverlayPos]  = useState<{ x: number; y: number } | null>(null);
    const [textValue,   setTextValue]   = useState('');
    const [canvasW, setCanvasW] = useState(800);
    const [canvasH, setCanvasH] = useState(500);
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    const [loading,    setLoading]    = useState(true);
    const [showCopyModal,  setShowCopyModal]  = useState(false);
    const [copyTargetIds,  setCopyTargetIds]  = useState<Set<number>>(new Set());
    const [copying,        setCopying]        = useState(false);
    const [copyStatus,     setCopyStatus]     = useState<'idle' | 'success' | 'error'>('idle');
    const [pinTick,        setPinTick]        = useState(0); // forces re-render during bolum drag
    const [hoveredBolumID, setHoveredBolumID] = useState<number | null>(null);

    // Image placement overlay
    const [imgOverlay,     setImgOverlay]     = useState(false);
    const [imgDataUrl,     setImgDataUrl]     = useState<string | null>(null);
    const [imgPos,         setImgPos]         = useState({ x: 40, y: 40 });
    const [imgSize,        setImgSize]        = useState({ w: 200, h: 150 });
    const [imgDragState,   setImgDragState]   = useState<{ mx: number; my: number; x: number; y: number } | null>(null);
    const [imgResizeState, setImgResizeState] = useState<{ mx: number; my: number; w: number; h: number } | null>(null);

    // Bölüm pin drag state
    const [bolumDrag, setBolumDrag] = useState<{
        bolumID: number;
        startMx: number; startMy: number;
        startXPct: number; startYPct: number;
    } | null>(null);

    // Aspect ratio: use kat en/boy if both exist, else 4/3
    const ratio = katEn && katBoy && katBoy > 0 ? katEn / katBoy : 4 / 3;

    // Legacy veriden normalize et (koordinatlar >1.5 ise mutlak piksel demektir)
    const normalizeShapes = (data: DrawShape[], cw: number, ch: number): DrawShape[] =>
        data.map(s => ({
            ...s,
            x1: s.x1 / cw, y1: s.y1 / ch,
            x2: s.x2 / cw, y2: s.y2 / ch,
        }));

    const isLegacyData = (data: DrawShape[]) =>
        data.some(s => Math.abs(s.x1) > 1.5 || Math.abs(s.x2) > 1.5 || Math.abs(s.y1) > 1.5 || Math.abs(s.y2) > 1.5);

    // Legacy verileri canvasW hazır olunca normalize etmek için ref
    const legacyShapesRef = React.useRef<DrawShape[] | null>(null);

    // Load saved drawing on mount
    useEffect(() => {
        setLoading(true);
        legacyShapesRef.current = null;
        axiosInstance.get<DrawShape[]>(`/api/KatCizim/${katId}`)
            .then(res => {
                const data = res.data ?? [];
                if (isLegacyData(data)) {
                    // canvasW henüz stabil olmayabilir; ref'e at, resize effect normalize eder
                    legacyShapesRef.current = data;
                    // Geçici: canvasW > 0 ise hemen normalize et
                    if (canvasW > 0 && canvasH > 0) {
                        const norm = normalizeShapes(data, canvasW, canvasH);
                        legacyShapesRef.current = null;
                        setShapes(norm);
                        norm.forEach(s => {
                            if (s.tool === 'image' && s.imageData && !imageCache.current.has(s.id)) {
                                const img = new Image();
                                img.src = s.imageData;
                                imageCache.current.set(s.id, img);
                            }
                        });
                    }
                } else {
                    // Zaten normalize
                    setShapes(data);
                    data.forEach(s => {
                        if (s.tool === 'image' && s.imageData && !imageCache.current.has(s.id)) {
                            const img = new Image();
                            img.src = s.imageData;
                            imageCache.current.set(s.id, img);
                        }
                    });
                }
            })
            .catch(() => setShapes([]))
            .finally(() => setLoading(false));
    }, [katId]); // eslint-disable-line react-hooks/exhaustive-deps

    // canvasW/H stabileşince legacy veriyi normalize et
    useEffect(() => {
        if (!legacyShapesRef.current || canvasW <= 0 || canvasH <= 0) return;
        const norm = normalizeShapes(legacyShapesRef.current, canvasW, canvasH);
        legacyShapesRef.current = null;
        setShapes(norm);
        norm.forEach(s => {
            if (s.tool === 'image' && s.imageData && !imageCache.current.has(s.id)) {
                const img = new Image();
                img.src = s.imageData;
                imageCache.current.set(s.id, img);
            }
        });
    }, [canvasW, canvasH]); // eslint-disable-line react-hooks/exhaustive-deps

    // Initialize pin positions from bolumler prop
    useEffect(() => {
        if (!bolumler) return;
        bolumler.forEach(b => {
            if (b.xVal != null && b.yVal != null) {
                pinPositions.current.set(b.bolumID, { x: b.xVal, y: b.yVal });
            } else if (!pinPositions.current.has(b.bolumID)) {
                // Default to visible position (5% from top-left)
                pinPositions.current.set(b.bolumID, { x: 0.05, y: 0.05 });
            }
        });
        setPinTick(t => t + 1);
    }, [bolumler]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Bölüm pin drag (window-level) ────────────────────────────────────────
    useEffect(() => {
        if (!bolumDrag) return;
        const onMove = (e: MouseEvent) => {
            if (!bolumDrag || !wrapperRef.current) return;
            const wrapper = wrapperRef.current;
            const dx = e.clientX - bolumDrag.startMx;
            const dy = e.clientY - bolumDrag.startMy;
            const newXPct = Math.min(Math.max(bolumDrag.startXPct + dx / wrapper.clientWidth, 0), 1);
            const newYPct = Math.min(Math.max(bolumDrag.startYPct + dy / wrapper.clientHeight, 0), 1);
            // update in pinPositions ref for live preview
            pinPositions.current.set(bolumDrag.bolumID, { x: newXPct, y: newYPct });
            setPinTick(t => t + 1);
        };
        const onUp = async (e: MouseEvent) => {
            if (!bolumDrag || !wrapperRef.current) return;
            const wrapper = wrapperRef.current;
            const dx = e.clientX - bolumDrag.startMx;
            const dy = e.clientY - bolumDrag.startMy;
            const newXPct = Math.min(Math.max(bolumDrag.startXPct + dx / wrapper.clientWidth, 0), 1);
            const newYPct = Math.min(Math.max(bolumDrag.startYPct + dy / wrapper.clientHeight, 0), 1);
            pinPositions.current.set(bolumDrag.bolumID, { x: newXPct, y: newYPct });
            setPinTick(t => t + 1);
            setBolumDrag(null);
            if (arsaId != null) {
                try {
                    await axiosInstance.patch(
                        `/api/KatKarsiligArsa/${arsaId}/bolumler/${bolumDrag.bolumID}/koordinat`,
                        { xVal: newXPct, yVal: newYPct }
                    );
                    onBolumKoordinatGuncellendi?.(bolumDrag.bolumID, newXPct, newYPct);
                } catch { /* ignore */ }
            }
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [bolumDrag]); // eslint-disable-line react-hooks/exhaustive-deps

    const saveDrawing = async () => {
        setSaveStatus('saving');
        try {
            await axiosInstance.post(`/api/KatCizim/${katId}`, shapes);
            setSaveStatus('saved');
        } catch {
            setSaveStatus('error');
        }
        setTimeout(() => setSaveStatus('idle'), 2500);
    };

    const handleCopy = async () => {
        if (copyTargetIds.size === 0) return;
        setCopying(true);
        setCopyStatus('idle');
        try {
            await Promise.all(
                Array.from(copyTargetIds).map(targetKatId =>
                    axiosInstance.post(`/api/KatCizim/${targetKatId}`, shapes)
                )
            );
            setCopyStatus('success');
            setTimeout(() => {
                setShowCopyModal(false);
                setCopyTargetIds(new Set());
                setCopyStatus('idle');
            }, 1500);
        } catch {
            setCopyStatus('error');
        } finally {
            setCopying(false);
        }
    };

    // Responsive canvas sizing — en/boy oranı HER ZAMAN korunur
    useEffect(() => {
        const update = () => {
            const el = wrapperRef.current;
            if (!el) return;
            // Parent'tan ölç: wrapper kendi genişliğine bağlı olunca geri besleme döngüsü oluşur
            const avail = (el.parentElement?.clientWidth ?? el.clientWidth) - 4;
            let w = avail > 0 ? avail : 800;
            let h = Math.round(w / ratio);
            const maxH = window.innerHeight * 0.75;
            if (h > maxH) {
                h = Math.floor(maxH);
                w = Math.round(h * ratio);
            }
            setCanvasW(w > 0 ? w : 800);
            setCanvasH(h > 0 ? h : Math.round(800 / ratio));
        };
        update();
        // Parent'ı izle, wrapper'ı değil — feedback loop engellenir
        const target = wrapperRef.current?.parentElement ?? wrapperRef.current;
        const ro = new ResizeObserver(update);
        if (target) ro.observe(target);
        return () => ro.disconnect();
    }, [ratio]);

    // ── Drawing helpers — koordinatlar [0,1] normalize, canvas piksellerine ölçeklen ─────────────

    const buildFont = (s: DrawShape) =>
        `${s.fontItalic ? 'italic ' : ''}${s.fontBold ? 'bold ' : ''}${s.fontSize ?? 14}px sans-serif`;

    const drawShape = (ctx: CanvasRenderingContext2D, s: DrawShape) => {
        ctx.save();
        ctx.strokeStyle = s.color;
        ctx.fillStyle = s.color;
        ctx.lineWidth = s.lineWidth;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';

        // Normalize [0,1] → canvas piksel
        const X1 = s.x1 * canvasW, Y1 = s.y1 * canvasH;
        const X2 = s.x2 * canvasW, Y2 = s.y2 * canvasH;
        const x = Math.min(X1, X2);
        const y = Math.min(Y1, Y2);
        const w = Math.abs(X2 - X1);
        const h = Math.abs(Y2 - Y1);

        switch (s.tool) {
            case 'rect':
                ctx.beginPath();
                ctx.rect(x, y, w, h);
                if (s.filled) ctx.fill(); else ctx.stroke();
                break;
            case 'oval':
                ctx.beginPath();
                ctx.ellipse(x + w / 2, y + h / 2, Math.max(w / 2, 1), Math.max(h / 2, 1), 0, 0, Math.PI * 2);
                if (s.filled) ctx.fill(); else ctx.stroke();
                break;
            case 'line':
                ctx.beginPath();
                ctx.moveTo(X1, Y1);
                ctx.lineTo(X2, Y2);
                ctx.stroke();
                break;
            case 'text':
                ctx.font = buildFont(s);
                ctx.fillText(s.text ?? '', X1, Y1);
                break;
            case 'image':
                if (s.imageData) {
                    let img = imageCache.current.get(s.id);
                    if (!img) {
                        img = new Image();
                        const captured = img;
                        captured.onload = () => redraw();
                        captured.src = s.imageData;
                        imageCache.current.set(s.id, captured);
                    }
                    if (img.complete && img.naturalWidth > 0) {
                        ctx.drawImage(img, X1, Y1, X2 - X1, Y2 - Y1);
                    }
                }
                break;
        }
        ctx.restore();
    };

    const redraw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Light grid
        ctx.save();
        ctx.strokeStyle = '#e8edf4';
        ctx.lineWidth = 0.5;
        const gs = 24;
        for (let x = 0; x <= canvas.width; x += gs) {
            ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke();
        }
        for (let y = 0; y <= canvas.height; y += gs) {
            ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvas.width, y); ctx.stroke();
        }
        ctx.restore();

        shapes.forEach(s => drawShape(ctx, s));
        if (previewShape) {
            ctx.save();
            ctx.globalAlpha = 0.65;
            drawShape(ctx, previewShape);
            ctx.restore();
        }
    }, [shapes, previewShape]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { redraw(); }, [redraw]);

    // ── Image drag / resize via window events ────────────────────────────────
    useEffect(() => {
        if (!imgDragState && !imgResizeState) return;
        const onMove = (e: MouseEvent) => {
            if (imgDragState) {
                const wrapper = wrapperRef.current;
                if (!wrapper) return;
                const maxX = wrapper.clientWidth  - imgSize.w;
                const maxY = wrapper.clientHeight - imgSize.h;
                setImgPos({
                    x: Math.min(Math.max(imgDragState.x + e.clientX - imgDragState.mx, 0), Math.max(maxX, 0)),
                    y: Math.min(Math.max(imgDragState.y + e.clientY - imgDragState.my, 0), Math.max(maxY, 0)),
                });
            }
            if (imgResizeState) {
                setImgSize({
                    w: Math.max(40, imgResizeState.w + e.clientX - imgResizeState.mx),
                    h: Math.max(30, imgResizeState.h + e.clientY - imgResizeState.my),
                });
            }
        };
        const onUp = () => { setImgDragState(null); setImgResizeState(null); };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [imgDragState, imgResizeState, imgSize.w, imgSize.h]);

    // ── Mouse event helpers ──────────────────────────────────────────────────

    const getPos = (e: React.MouseEvent<HTMLCanvasElement>) => {
        const rect = canvasRef.current!.getBoundingClientRect();
        // Doğrudan [0,1] normalize — canvas aspect ratio her zaman korunduğundan
        // rect.width=canvasW, rect.height=canvasH (CSS ile garanti altında)
        return {
            x: Math.min(Math.max((e.clientX - rect.left) / rect.width,  0), 1),
            y: Math.min(Math.max((e.clientY - rect.top)  / rect.height, 0), 1),
        };
    };

    const getOverlayPos = (e: React.MouseEvent<HTMLCanvasElement>) => {
        const wrapper = wrapperRef.current!;
        const rect = wrapper.getBoundingClientRect();
        const padding = 8;
        return {
            x: Math.min(Math.max(e.clientX - rect.left, padding), rect.width - 220),
            y: Math.min(Math.max(e.clientY - rect.top, padding), rect.height - 120),
        };
    };

    const onMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (textOverlay || imgOverlay) return;
        if (tool === 'text') {
            setDrawPos(getPos(e));
            setOverlayPos(getOverlayPos(e));
            setTextValue('');
            setTextOverlay(true);
            return;
        }
        if (tool === 'image') {
            fileInputRef.current?.click();
            return;
        }
        setIsDrawing(true);
        setStartPos(getPos(e));
    };

    const onMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!isDrawing || !startPos || tool === 'text') return;
        const pos = getPos(e);
        setPreviewShape({ id: -1, tool, color, filled, lineWidth: 2, x1: startPos.x, y1: startPos.y, x2: pos.x, y2: pos.y });
    };

    const onMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!isDrawing || !startPos || tool === 'text') return;
        const pos = getPos(e);
        setShapes(prev => [...prev, { id: Date.now(), tool, color, filled, lineWidth: 2, x1: startPos.x, y1: startPos.y, x2: pos.x, y2: pos.y }]);
        setPreviewShape(null);
        setIsDrawing(false);
        setStartPos(null);
    };

    const commitText = () => {
        if (drawPos && textValue.trim()) {
            setShapes(prev => [...prev, {
                id: Date.now(), tool: 'text', color,
                filled: false, lineWidth: 1,
                // drawPos zaten [0,1] normalize (getPos'tan geliyor)
                x1: drawPos.x, y1: drawPos.y,
                x2: drawPos.x, y2: drawPos.y,
                text: textValue, fontSize, fontBold, fontItalic,
            }]);
        }
        setTextOverlay(false);
        setTextValue('');
        setDrawPos(null);
        setOverlayPos(null);
    };

    const cancelText = () => {
        setTextOverlay(false);
        setTextValue('');
        setDrawPos(null);
        setOverlayPos(null);
    };

    // ── Image helpers ────────────────────────────────────────────────────────

    const cssToCanvas = (cssX: number, cssY: number) => {
        const canvas  = canvasRef.current!;
        const wrapper = wrapperRef.current!;
        return {
            x: cssX * (canvas.width  / wrapper.clientWidth),
            y: cssY * (canvas.height / wrapper.clientHeight),
        };
    };

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = ev => {
            const dataUrl = ev.target?.result as string;
            const img = new Image();
            img.onload = () => {
                const maxW = (wrapperRef.current?.clientWidth ?? 400) * 0.55;
                const scale = Math.min(1, maxW / img.naturalWidth);
                setImgSize({ w: Math.round(img.naturalWidth * scale), h: Math.round(img.naturalHeight * scale) });
                setImgPos({ x: 40, y: 40 });
                setImgDataUrl(dataUrl);
                setImgOverlay(true);
            };
            img.src = dataUrl;
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const commitImage = () => {
        if (!imgDataUrl) return;
        const tl = cssToCanvas(imgPos.x, imgPos.y);
        const br = cssToCanvas(imgPos.x + imgSize.w, imgPos.y + imgSize.h);
        const id = Date.now();
        const img = new Image();
        img.src = imgDataUrl;
        imageCache.current.set(id, img);
        setShapes(prev => [...prev, {
            id, tool: 'image', color: '#000', filled: false, lineWidth: 1,
            // cssToCanvas piksel → normalize
            x1: tl.x / canvasW, y1: tl.y / canvasH,
            x2: br.x / canvasW, y2: br.y / canvasH,
            imageData: imgDataUrl,
        }]);
        setImgOverlay(false);
        setImgDataUrl(null);
    };

    const cancelImage = () => {
        setImgOverlay(false);
        setImgDataUrl(null);
    };

    // ── Tool button style ────────────────────────────────────────────────────

    const toolBtn = (t: DrawTool, label: React.ReactNode) => (
        <button
            key={t}
            onClick={() => setTool(t)}
            style={{
                padding: '5px 13px', fontSize: '0.8rem', borderRadius: 5, cursor: 'pointer',
                background: tool === t ? '#1a2340' : '#f0f4ff',
                color: tool === t ? '#fff' : '#1a2340',
                border: `1px solid ${tool === t ? '#1a2340' : '#c8d4ee'}`,
                fontWeight: tool === t ? 700 : 400,
                whiteSpace: 'nowrap',
            }}
        >{label}</button>
    );

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <div style={{ padding: '16px 20px', height: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
            {/* Hidden file input for image upload */}
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFileChange} />

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
                <span style={{ fontWeight: 700, fontSize: '1rem', color: '#1a2340' }}>{blokAdi}</span>
                <span style={{ color: '#aaa', fontSize: '1rem' }}>›</span>
                <span style={{ fontWeight: 600, fontSize: '1rem', color: '#3b6bb5' }}>{katAdi}</span>
                {katEn && katBoy && (
                    <span style={{ fontSize: '0.78rem', color: '#999', marginLeft: 4 }}>({katEn}×{katBoy})</span>
                )}
            </div>

            {/* Toolbar */}
            <div style={{ display: 'flex', gap: 7, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center', background: '#f7f9ff', padding: '8px 10px', borderRadius: 7, border: '1px solid #e4eaf6' }}>
                {toolBtn('rect', <><i className="far fa-square" style={{ marginRight: 5 }}></i>Dikdörtgen</>)}
                {toolBtn('oval', <><i className="far fa-circle" style={{ marginRight: 5 }}></i>Oval</>)}
                {toolBtn('line', <><i className="fas fa-minus" style={{ marginRight: 5 }}></i>Çizgi</>)}
                {toolBtn('text', <><i className="fas fa-font" style={{ marginRight: 5 }}></i>Metin</>)}
                {toolBtn('image', <><i className="fas fa-image" style={{ marginRight: 5 }}></i>Resim</>)}

                <div style={{ width: 1, height: 24, background: '#dce4f0', margin: '0 2px' }} />

                <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.8rem', cursor: 'pointer' }}>
                    Renk:
                    <input
                        type="color" value={color}
                        onChange={e => setColor(e.target.value)}
                        style={{ width: 34, height: 28, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: 2 }}
                    />
                </label>

                {(tool === 'rect' || tool === 'oval') && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.8rem', cursor: 'pointer', userSelect: 'none' }}>
                        <input type="checkbox" checked={filled} onChange={e => setFilled(e.target.checked)} style={{ width: 14, height: 14 }} />
                        Dolu
                    </label>
                )}

                {tool === 'text' && (
                    <>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem' }}>
                            Boyut:
                            <input
                                type="number" value={fontSize} min={8} max={72} step={1}
                                onChange={e => setFontSize(Math.max(8, Math.min(72, Number(e.target.value))))}
                                style={{ width: 50, padding: '2px 5px', borderRadius: 4, border: '1px solid #c8d4ee', fontSize: '0.8rem' }}
                            />
                        </label>
                        <button
                            onClick={() => setFontBold(v => !v)}
                            style={{
                                padding: '4px 11px', fontSize: '0.82rem', borderRadius: 5, cursor: 'pointer', fontWeight: 700,
                                background: fontBold ? '#1a2340' : '#f0f4ff',
                                color: fontBold ? '#fff' : '#1a2340',
                                border: `1px solid ${fontBold ? '#1a2340' : '#c8d4ee'}`,
                            }}
                        >K</button>
                        <button
                            onClick={() => setFontItalic(v => !v)}
                            style={{
                                padding: '4px 11px', fontSize: '0.82rem', borderRadius: 5, cursor: 'pointer', fontStyle: 'italic',
                                background: fontItalic ? '#1a2340' : '#f0f4ff',
                                color: fontItalic ? '#fff' : '#1a2340',
                                border: `1px solid ${fontItalic ? '#1a2340' : '#c8d4ee'}`,
                            }}
                        ><i>İ</i></button>
                    </>
                )}

                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                    {saveStatus === 'saved'  && <span style={{ fontSize: '0.75rem', color: '#27ae60' }}><i className="fas fa-check" style={{ marginRight: 4 }}></i>Kaydedildi</span>}
                    {saveStatus === 'error'  && <span style={{ fontSize: '0.75rem', color: '#c0392b' }}><i className="fas fa-times" style={{ marginRight: 4 }}></i>Hata</span>}
                    {blokKatlar && blokKatlar.filter(k => k.katID !== katId).length > 0 && (
                        <button
                            onClick={() => { setCopyTargetIds(new Set()); setCopyStatus('idle'); setShowCopyModal(true); }}
                            style={{ padding: '5px 12px', fontSize: '0.8rem', borderRadius: 5, border: '1px solid #7c3aed', cursor: 'pointer', background: '#f5f0ff', color: '#6d28d9', whiteSpace: 'nowrap' }}
                        ><i className="fas fa-copy" style={{ marginRight: 5 }}></i>Taslağı Kopyala</button>
                    )}
                    <button
                        onClick={saveDrawing}
                        disabled={saveStatus === 'saving'}
                        style={{ padding: '5px 12px', fontSize: '0.8rem', borderRadius: 5, border: '1px solid #3b6bb5', cursor: 'pointer', background: '#1a2340', color: '#fff', opacity: saveStatus === 'saving' ? 0.6 : 1 }}
                    >{saveStatus === 'saving' ? '...' : <><i className="fas fa-save" style={{ marginRight: 5 }}></i>Kaydet</>}</button>
                    <button
                        onClick={() => setShapes(prev => prev.slice(0, -1))}
                        disabled={shapes.length === 0}
                        style={{ padding: '5px 10px', fontSize: '0.8rem', borderRadius: 5, border: '1px solid #ddd', cursor: 'pointer', background: '#fff', opacity: shapes.length === 0 ? 0.4 : 1 }}
                    ><i className="fas fa-undo" style={{ marginRight: 5 }}></i>Geri Al</button>
                    <button
                        onClick={() => setShapes([])}
                        disabled={shapes.length === 0}
                        style={{ padding: '5px 10px', fontSize: '0.8rem', borderRadius: 5, border: '1px solid #fcc', cursor: 'pointer', background: '#fff5f5', color: '#c0392b', opacity: shapes.length === 0 ? 0.4 : 1 }}
                    ><i className="fas fa-times" style={{ marginRight: 5 }}></i>Temizle</button>
                </div>
            </div>

            {/* Canvas wrapper — genişlik canvasW ile sınırlandırılır, oran bozulmaz */}
            <div ref={wrapperRef} style={{ position: 'relative', border: '2px solid #c8d4ee', borderRadius: 6, overflow: 'hidden', background: '#fff', display: 'block', width: canvasW }}>
                {loading && (
                    <div style={{ position: 'absolute', inset: 0, zIndex: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.75)', fontSize: '0.85rem', color: '#555' }}>
                        Çizim yükleniyor...
                    </div>
                )}
                <canvas
                    ref={canvasRef}
                    width={canvasW}
                    height={canvasH}
                    style={{ display: 'block', width: '100%', height: canvasH, cursor: tool === 'text' ? 'text' : tool === 'image' ? 'cell' : 'crosshair' }}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={() => { setIsDrawing(false); setPreviewShape(null); }}
                />

                {/* Bölüm pin overlays */}
                {bolumler && bolumler.map(b => {
                    const pos = pinPositions.current.get(b.bolumID);
                    if (!pos) return null;
                    const isDragging = bolumDrag?.bolumID === b.bolumID;
                    const hasPaylasim = (b.paylasimlar?.length ?? 0) > 0;
                    const isHovered = hoveredBolumID === b.bolumID && !isDragging;

                    const bgColor = isDragging
                        ? '#3b6bb5'
                        : hasPaylasim
                            ? 'rgba(22,120,60,0.92)'
                            : 'rgba(26,35,64,0.88)';
                    const dotColor = isDragging ? '#3b6bb5' : hasPaylasim ? '#16783c' : '#1a2340';
                    const borderColor = isDragging ? '#2b5aa5' : hasPaylasim ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.2)';

                    return (
                        <div
                            key={b.bolumID}
                            style={{
                                position: 'absolute',
                                left: `${pos.x * 100}%`,
                                top: `${pos.y * 100}%`,
                                zIndex: isHovered ? 20 : 15,
                                transform: 'translate(-50%, -50%)',
                                cursor: isDragging ? 'grabbing' : 'grab',
                                userSelect: 'none',
                                pointerEvents: 'auto',
                            }}
                            onMouseEnter={() => setHoveredBolumID(b.bolumID)}
                            onMouseLeave={() => setHoveredBolumID(null)}
                            onMouseDown={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                setBolumDrag({
                                    bolumID: b.bolumID,
                                    startMx: e.clientX,
                                    startMy: e.clientY,
                                    startXPct: pos.x,
                                    startYPct: pos.y,
                                });
                            }}
                        >
                            <div style={{
                                background: bgColor,
                                color: '#fff',
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                padding: '3px 8px',
                                borderRadius: 4,
                                whiteSpace: 'nowrap',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                border: `1px solid ${borderColor}`,
                                letterSpacing: '0.01em',
                            }}>
                                {b.bolumAdi}
                            </div>
                            {/* connector dot */}
                            <div style={{
                                position: 'absolute',
                                left: '50%', top: '100%',
                                transform: 'translate(-50%, 0)',
                                width: 6, height: 6,
                                borderRadius: '50%',
                                background: dotColor,
                                marginTop: 1,
                            }} />
                            {/* Hover tooltip — paylaşımlar */}
                            {isHovered && hasPaylasim && (
                                <div style={{
                                    position: 'absolute',
                                    bottom: 'calc(100% + 10px)',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    background: 'rgba(15,25,50,0.96)',
                                    color: '#fff',
                                    fontSize: '0.75rem',
                                    borderRadius: 6,
                                    padding: '8px 12px',
                                    minWidth: 160,
                                    maxWidth: 260,
                                    boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
                                    pointerEvents: 'none',
                                    zIndex: 25,
                                    whiteSpace: 'normal',
                                }}>
                                    <div style={{ fontWeight: 700, marginBottom: 6, color: '#7ee8a2', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Paylaşımlar
                                    </div>
                                    {b.paylasimlar!.map((p, i) => (
                                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, lineHeight: '1.6', borderTop: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.08)', paddingTop: i === 0 ? 0 : 4 }}>
                                            <span style={{ color: '#d1e8ff' }}>{p.adSoyad}</span>
                                            <span style={{ color: '#7ee8a2', fontWeight: 700, flexShrink: 0 }}>%{p.oranYuzde.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
                                        </div>
                                    ))}
                                    {/* Tooltip arrow */}
                                    <div style={{
                                        position: 'absolute',
                                        bottom: -6,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: 0, height: 0,
                                        borderLeft: '6px solid transparent',
                                        borderRight: '6px solid transparent',
                                        borderTop: '6px solid rgba(15,25,50,0.96)',
                                    }} />
                                </div>
                            )}
                            {/* Hover tooltip — paylaşım yok */}
                            {isHovered && !hasPaylasim && (
                                <div style={{
                                    position: 'absolute',
                                    bottom: 'calc(100% + 10px)',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    background: 'rgba(15,25,50,0.96)',
                                    color: '#bbb',
                                    fontSize: '0.73rem',
                                    borderRadius: 6,
                                    padding: '6px 10px',
                                    whiteSpace: 'nowrap',
                                    boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
                                    pointerEvents: 'none',
                                    zIndex: 25,
                                }}>
                                    Paylaşım yapılmadı
                                    <div style={{
                                        position: 'absolute',
                                        bottom: -6,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: 0, height: 0,
                                        borderLeft: '6px solid transparent',
                                        borderRight: '6px solid transparent',
                                        borderTop: '6px solid rgba(15,25,50,0.96)',
                                    }} />
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Cephe etiketleri */}
                {cepheler && cepheler.map((c, i) => {
                    const yon = (c.yon ?? '').trim().toLowerCase();
                    let style: React.CSSProperties = {
                        position: 'absolute',
                        background: '#e67e22',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.78rem',
                        padding: '5px 15px',
                        borderRadius: 6,
                        pointerEvents: 'none',
                        userSelect: 'none',
                        zIndex: 10,
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.22)',
                    };
                    if (yon === 'kuzey' || yon === 'k') {
                        style = { ...style, top: 8, left: '50%', transform: 'translateX(-50%)' };
                    } else if (yon === 'güney' || yon === 'guney' || yon === 'g') {
                        style = { ...style, bottom: 8, left: '50%', transform: 'translateX(-50%)' };
                    } else if (yon === 'batı' || yon === 'bati' || yon === 'b') {
                        style = { ...style, left: 8, top: '50%', transform: 'translateY(-50%) rotate(-90deg)', transformOrigin: 'center center' };
                    } else if (yon === 'doğu' || yon === 'dogu' || yon === 'd') {
                        style = { ...style, right: 8, top: '50%', transform: 'translateY(-50%) rotate(90deg)', transformOrigin: 'center center' };
                    } else {
                        return null;
                    }
                    return <div key={i} style={style}>{c.cepheAdi}</div>;
                })}

                {/* Compass */}
                <div style={{
                    position: 'absolute', top: 8, right: 8,
                    userSelect: 'none', pointerEvents: 'none',
                }}>
                    <img src={yonlerImg} alt="Yönler" style={{ width: 72, height: 72, display: 'block', opacity: 0.88 }} />
                </div>

                {/* Text overlay popup */}
                {textOverlay && overlayPos && (
                    <div
                        style={{
                            position: 'absolute',
                            left: overlayPos.x,
                            top: overlayPos.y,
                            zIndex: 20,
                            background: 'rgba(255,255,255,0.97)',
                            border: '1px solid #3b6bb5',
                            borderRadius: 7,
                            padding: '8px 10px',
                            minWidth: 210,
                            boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
                        }}
                    >
                        {/* Mini font toolbar */}
                        <div style={{ display: 'flex', gap: 5, marginBottom: 6, alignItems: 'center' }}>
                            <input
                                type="number" value={fontSize} min={8} max={72} step={1}
                                onChange={e => setFontSize(Math.max(8, Math.min(72, Number(e.target.value))))}
                                onMouseDown={e => e.stopPropagation()}
                                style={{ width: 46, padding: '2px 4px', borderRadius: 4, border: '1px solid #c8d4ee', fontSize: '0.78rem' }}
                            />
                            <button
                                onMouseDown={e => { e.preventDefault(); setFontBold(v => !v); }}
                                style={{
                                    padding: '2px 9px', fontSize: '0.82rem', borderRadius: 4, cursor: 'pointer', fontWeight: 700,
                                    background: fontBold ? '#1a2340' : '#f0f4ff',
                                    color: fontBold ? '#fff' : '#1a2340',
                                    border: `1px solid ${fontBold ? '#1a2340' : '#c8d4ee'}`,
                                }}
                            >K</button>
                            <button
                                onMouseDown={e => { e.preventDefault(); setFontItalic(v => !v); }}
                                style={{
                                    padding: '2px 9px', fontSize: '0.82rem', borderRadius: 4, cursor: 'pointer', fontStyle: 'italic',
                                    background: fontItalic ? '#1a2340' : '#f0f4ff',
                                    color: fontItalic ? '#fff' : '#1a2340',
                                    border: `1px solid ${fontItalic ? '#1a2340' : '#c8d4ee'}`,
                                }}
                            ><i>İ</i></button>
                            <span style={{ display: 'inline-block', width: 20, height: 20, borderRadius: 3, background: color, border: '1px solid #ccc', marginLeft: 2 }} />
                        </div>
                        {/* Text input */}
                        <input
                            ref={textInputRef}
                            autoFocus
                            value={textValue}
                            onChange={e => setTextValue(e.target.value)}
                            onKeyDown={e => {
                                e.stopPropagation();
                                if (e.key === 'Enter') commitText();
                                if (e.key === 'Escape') cancelText();
                            }}
                            placeholder="Metin girin..."
                            style={{
                                width: '100%',
                                boxSizing: 'border-box',
                                border: '1px solid #aaa',
                                borderRadius: 4,
                                padding: '3px 7px',
                                fontSize: Math.min(fontSize, 20),
                                fontWeight: fontBold ? 700 : 400,
                                fontStyle: fontItalic ? 'italic' : 'normal',
                                color: color,
                                outline: 'none',
                                marginBottom: 7,
                            }}
                        />
                        {/* Action buttons */}
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button
                                onMouseDown={e => { e.preventDefault(); cancelText(); }}
                                style={{ padding: '3px 12px', fontSize: '0.78rem', borderRadius: 4, border: '1px solid #ddd', cursor: 'pointer', background: '#f5f5f5' }}
                            >İptal</button>
                            <button
                                onMouseDown={e => { e.preventDefault(); commitText(); }}
                                style={{ padding: '3px 12px', fontSize: '0.78rem', borderRadius: 4, border: '1px solid #3b6bb5', cursor: 'pointer', background: '#1a2340', color: '#fff' }}
                            >Ekle ↵</button>
                        </div>
                    </div>
                )}

                {/* Image placement overlay */}
                {imgOverlay && imgDataUrl && (
                    <div style={{ position: 'absolute', inset: 0, zIndex: 25 }}>
                        {/* Draggable image frame */}
                        <div
                            style={{
                                position: 'absolute',
                                left: imgPos.x, top: imgPos.y,
                                width: imgSize.w, height: imgSize.h,
                                cursor: imgDragState ? 'grabbing' : 'grab',
                                userSelect: 'none',
                            }}
                            onMouseDown={e => {
                                e.preventDefault();
                                setImgDragState({ mx: e.clientX, my: e.clientY, x: imgPos.x, y: imgPos.y });
                            }}
                        >
                            <img
                                src={imgDataUrl}
                                alt="yerleşim"
                                draggable={false}
                                style={{ width: '100%', height: '100%', display: 'block', border: '2px dashed #3b6bb5', boxSizing: 'border-box', pointerEvents: 'none' }}
                            />
                            {/* Resize handle — bottom-right */}
                            <div
                                style={{
                                    position: 'absolute', bottom: -6, right: -6,
                                    width: 14, height: 14,
                                    background: '#3b6bb5', borderRadius: 3,
                                    cursor: 'se-resize',
                                }}
                                onMouseDown={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setImgResizeState({ mx: e.clientX, my: e.clientY, w: imgSize.w, h: imgSize.h });
                                }}
                            />
                        </div>
                        {/* Action buttons — positioned below the image */}
                        <div style={{
                            position: 'absolute',
                            left: imgPos.x,
                            top: imgPos.y + imgSize.h + 10,
                            display: 'flex', gap: 6,
                        }}>
                            <button
                                onMouseDown={e => { e.preventDefault(); cancelImage(); }}
                                style={{ padding: '4px 14px', fontSize: '0.78rem', borderRadius: 4, border: '1px solid #ddd', cursor: 'pointer', background: 'rgba(245,245,245,0.95)' }}
                            >İptal</button>
                            <button
                                onMouseDown={e => { e.preventDefault(); commitImage(); }}
                                style={{ padding: '4px 14px', fontSize: '0.78rem', borderRadius: 4, border: '1px solid #3b6bb5', cursor: 'pointer', background: '#1a2340', color: '#fff' }}
                            >Yerleştir <i className="fas fa-check"></i></button>
                        </div>
                    </div>
                )}
            </div>

            {shapes.length > 0 && (
                <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#aaa' }}>
                    {shapes.length} şekil çizildi
                </div>
            )}

            {/* Taslağı Kopyala Modal */}
            {showCopyModal && blokKatlar && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.38)' }}>
                    <div style={{ background: '#fff', borderRadius: 10, padding: '24px 28px', minWidth: 300, maxWidth: 420, boxShadow: '0 6px 32px rgba(0,0,0,0.22)', width: '90%' }}>
                        <h3 style={{ margin: '0 0 8px', fontSize: '1rem', color: '#1a2340' }}>Taslağı Kopyala</h3>
                        <p style={{ margin: '0 0 14px', fontSize: '0.82rem', color: '#666' }}>
                            <strong>{katAdi}</strong> katının çizimini aşağıdaki katlara kopyalayabilirsiniz.
                            Kopyalama sonrası her katın çizimi birbirinden bağımsızdır.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16, maxHeight: 260, overflowY: 'auto' }}>
                            {blokKatlar.filter(k => k.katID !== katId).map(k => (
                                <label
                                    key={k.katID}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                                        fontSize: '0.88rem', padding: '8px 10px', borderRadius: 6,
                                        background: copyTargetIds.has(k.katID) ? '#eef3ff' : '#f9fafb',
                                        border: `1px solid ${copyTargetIds.has(k.katID) ? '#3b6bb5' : '#e5e7eb'}`,
                                        userSelect: 'none',
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={copyTargetIds.has(k.katID)}
                                        onChange={e => {
                                            const next = new Set(copyTargetIds);
                                            if (e.target.checked) next.add(k.katID); else next.delete(k.katID);
                                            setCopyTargetIds(next);
                                        }}
                                        style={{ width: 15, height: 15, accentColor: '#3b6bb5', cursor: 'pointer' }}
                                    />
                                    {k.katAdi}
                                </label>
                            ))}
                        </div>
                        {copyStatus === 'success' && (
                            <p style={{ color: '#27ae60', fontSize: '0.82rem', margin: '0 0 10px' }}>✓ Çizim seçili katlara kopyalandı!</p>
                        )}
                        {copyStatus === 'error' && (
                            <p style={{ color: '#c0392b', fontSize: '0.82rem', margin: '0 0 10px' }}>✕ Kopyalama sırasında hata oluştu.</p>
                        )}
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => { setShowCopyModal(false); setCopyTargetIds(new Set()); setCopyStatus('idle'); }}
                                disabled={copying}
                                style={{ padding: '6px 18px', fontSize: '0.85rem', borderRadius: 6, border: '1px solid #ddd', cursor: 'pointer', background: '#f5f5f5', color: '#555' }}
                            >İptal</button>
                            <button
                                onClick={handleCopy}
                                disabled={copying || copyTargetIds.size === 0}
                                style={{
                                    padding: '6px 18px', fontSize: '0.85rem', borderRadius: 6,
                                    border: '1px solid #7c3aed', cursor: copyTargetIds.size === 0 ? 'not-allowed' : 'pointer',
                                    background: copyTargetIds.size === 0 ? '#e9d5ff' : '#6d28d9',
                                    color: '#fff',
                                    opacity: copying ? 0.6 : 1,
                                }}
                            >{copying ? 'Kopyalanıyor...' : 'Kopyala'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default KatCizimPanel;
